// A bundle (docs/design/bundles.md): alike review cards shown as one decision, "7 revised
// assignments to acknowledge". The list shows one row ("Together"); the pane (and narrow screens,
// inline) shows the members with their draft state, Draft all (the owner's draft request for each
// member without a draft, one after another) and Review drafts (focus mode over the members).
// Nothing here sends: each reply is still its own card, armed and confirmed on its own.
//
// Per-member draft state: the board's CardSummary has no `drafting` field, so the block loads each
// member's full card (the same query a card uses, so it also warms focus mode) and reads its
// blocks and newest draft work; Draft all's own answers fill in until a card has loaded.
import { memo, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { PenLine } from "lucide-react";
import { toast } from "sonner";
import type { CardSummary, DraftRequestStatus } from "@shared/api";
import { isApiError } from "@web/lib/api";
import { cn } from "@web/lib/cn";
import { feedApi } from "@web/lib/feedApi";
import { acceptCard, cardQuery, invalidateBoards } from "@web/lib/feedQueries";
import { Button } from "@web/ui/Button";
import {
  draftAllWords,
  draftedWords,
  draftStateWord,
  memberDraftState,
  wantsDraft,
  writingWords,
  type MemberDraftState,
} from "./bundleWords";
import type { BoardBundle } from "./selectors";
import { rowSecondLine } from "./wording";

export const BUNDLE_KICKER = "Together";

/** The compact list-column row (wide screens show the block beside the list). */
export const BundleRow = memo(function BundleRow({
  bundle,
  id,
  selected,
  onSelect,
}: {
  bundle: BoardBundle;
  id: string;
  selected: boolean;
  onSelect: () => void;
}) {
  const drafted = draftedWords(bundle.drafted, bundle.total);
  return (
    <div
      id={id}
      role="option"
      aria-selected={selected}
      onClick={onSelect}
      data-bundle={bundle.id}
      className={cn(
        "relative flex cursor-pointer flex-col gap-0.5 border-b border-line px-4 py-3 select-none md:px-5",
        selected ? "bg-accent-soft" : "bg-memo/60 hover:bg-sunk/50",
      )}
    >
      {selected ? (
        <span aria-hidden className="absolute inset-y-0 left-0 w-[3px] bg-accent" />
      ) : null}
      <span className="mm-kicker !text-[10px]">{BUNDLE_KICKER}</span>
      <p className="line-clamp-2 font-serif text-[16px] leading-snug font-semibold text-ink">
        {bundle.label}
      </p>
      {bundle.summary ? <p className="line-clamp-2 text-sm text-muted">{bundle.summary}</p> : null}
      {drafted ? <p className="mt-1 text-xs text-muted">{drafted}</p> : null}
    </div>
  );
});

/** The members' draft states: each full card when loaded, else Draft all's answer for it. */
function useMemberStates(
  cards: readonly CardSummary[],
  asked: Readonly<Record<string, DraftRequestStatus>>,
): MemberDraftState[] {
  const results = useQueries({ queries: cards.map((c) => cardQuery(c.id)) });
  return cards.map((c, i) => memberDraftState(results[i]?.data, asked[c.id] ?? null));
}

const LINK_BUTTON =
  "inline-flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-lg border px-3 text-sm font-medium whitespace-nowrap transition-colors";

export interface BundleBlockProps {
  bundle: BoardBundle;
  /** DOM id for scrolling and aria-activedescendant (narrow screens list it inline). */
  id?: string;
  selected?: boolean;
  onSelect?: () => void;
  /** A member pressed: handle that card on its own (the pane shows it, or its page opens). */
  onSelectCard: (cardId: string) => void;
}

/** The full bundle (side pane, narrow screens). */
export function BundleBlock({
  bundle,
  id,
  selected = false,
  onSelect,
  onSelectCard,
}: BundleBlockProps) {
  const queryClient = useQueryClient();
  const [asked, setAsked] = useState<Record<string, DraftRequestStatus>>({});
  const [progress, setProgress] = useState<{ at: number; of: number } | null>(null);
  const states = useMemberStates(bundle.cards, asked);
  const todo = useMemo(
    () => bundle.cards.filter((_, i) => wantsDraft(states[i] ?? "unknown")),
    [bundle.cards, states],
  );
  const allDrafted =
    bundle.drafted >= bundle.total || states.every((s) => s === "ready" || s === "question");
  const drafted = draftedWords(bundle.drafted, bundle.total);

  const draftAll = async () => {
    if (progress || !todo.length) return;
    const cards = [...todo];
    try {
      for (const [index, card] of cards.entries()) {
        setProgress({ at: index + 1, of: cards.length });
        try {
          const res = await feedApi.draft(card.id);
          acceptCard(queryClient, res.card);
          setAsked((prev) => ({ ...prev, [card.id]: res.status }));
        } catch (error) {
          if (isApiError(error, "not_available")) {
            toast("Claude isn't connected, so it can't draft these.");
            return;
          }
          toast(`Couldn't ask for a draft for “${card.title}”. Going on with the rest.`);
        }
      }
    } finally {
      setProgress(null);
      invalidateBoards(queryClient, bundle.cards[0]?.feedId);
    }
  };

  return (
    <article
      id={id}
      aria-label={`Together: ${bundle.label}`}
      aria-current={selected || undefined}
      data-bundle={bundle.id}
      onClick={onSelect}
      className={cn(
        "relative rounded-xl border bg-memo/60 px-4 py-3",
        selected ? "border-accent/60 ring-1 ring-accent/40" : "border-line",
      )}
    >
      {selected ? (
        <span aria-hidden className="absolute inset-y-3 left-0 w-[3px] rounded-r bg-accent" />
      ) : null}
      <div className="flex flex-col gap-3">
        <div className="min-w-0">
          <p className="mm-kicker">{BUNDLE_KICKER}</p>
          <h3 className="text-lg leading-snug">{bundle.label}</h3>
          {bundle.summary ? <p className="text-sm text-ink-2">{bundle.summary}</p> : null}
          {drafted ? <p className="mt-0.5 text-xs text-muted">{drafted}</p> : null}
        </div>
        <ul className="flex flex-col divide-y divide-line border-y border-line">
          {bundle.cards.map((card, i) => (
            <BundleMember
              key={card.id}
              card={card}
              state={states[i] ?? "unknown"}
              onSelect={() => onSelectCard(card.id)}
            />
          ))}
        </ul>
        <div className="flex flex-wrap items-center gap-2">
          {todo.length || progress ? (
            <Button
              size="sm"
              variant={allDrafted ? "secondary" : "primary"}
              busy={!!progress}
              onClick={(e) => {
                e.stopPropagation();
                void draftAll();
              }}
            >
              <PenLine aria-hidden className="size-3.5" />
              {draftAllWords(todo.length)}
            </Button>
          ) : null}
          <Link
            to="/focus"
            search={{ bundle: bundle.id }}
            onClick={(e) => e.stopPropagation()}
            className={cn(
              LINK_BUTTON,
              allDrafted
                ? "border-transparent bg-accent text-accent-ink hover:brightness-105"
                : "border-line-strong bg-paper text-ink hover:bg-sunk",
            )}
          >
            Review drafts
          </Link>
          {progress ? (
            <span className="text-sm text-muted" aria-live="polite">
              {writingWords(progress.at, progress.of)}
            </span>
          ) : null}
        </div>
        <p className="text-xs text-muted">
          Each reply is still sent on its own card, after you confirm it.
        </p>
      </div>
    </article>
  );
}

function BundleMember({
  card,
  state,
  onSelect,
}: {
  card: CardSummary;
  state: MemberDraftState;
  onSelect: () => void;
}) {
  const ask = rowSecondLine(card);
  const word = draftStateWord(state);
  return (
    <li>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onSelect();
        }}
        className="flex w-full flex-col gap-0.5 px-1 py-2 text-left hover:bg-sunk/50"
      >
        <span className="flex min-w-0 items-center gap-2">
          <span className="mm-kicker truncate !text-[10px]">{card.eyebrow}</span>
          {word ? (
            <span
              data-draft-state={state}
              className={cn(
                "ml-auto shrink-0 text-xs",
                state === "ready" ? "text-calm" : "text-muted",
                state === "writing" && "animate-pulse",
              )}
            >
              {word}
            </span>
          ) : null}
        </span>
        <span className="line-clamp-1 text-sm font-medium text-ink">{card.title}</span>
        <span className={cn("line-clamp-1 text-sm", ask.isAsk ? "text-ink-2" : "text-muted")}>
          {ask.text}
        </span>
      </button>
    </li>
  );
}
